<template>
	<div>
		<h1>List of Support Requests</h1>
		<v-btn @click="onNavigateSummaryStats">View Summary Stats</v-btn>
		<div id="filters">
			<v-select
				:items="filterItems.topics"
				item-text="label"
				item-value="value"
				v-model="filterItems.selectedTopicType"
				label="Topic"
				outlined
				@input="onFilterInputChange"
			></v-select>
			<v-select
				:items="filterItems.departments"
				item-text="label"
				item-value="value"
				v-model="filterItems.selectedDepartment"
				label="Department"
				outlined
				@input="onFilterInputChange"
			></v-select>
			<v-select
				:items="computedAgencyFilterItems"
				item-text="label"
				item-value="value"
				v-model="filterItems.selectedAgency"
				label="Agency"
				outlined
				@input="onFilterInputChange"
			></v-select>
			<v-select
				:items="filterItems.contactMethods"
				item-text="label"
				item-value="value"
				v-model="filterItems.selectedContactType"
				label="Contact method"
				outlined
				@input="onFilterInputChange"
			></v-select>
			<v-select
				:items="filterItems.requestors"
				item-text="label"
				item-value="value"
				v-model="filterItems.selectedRequestor"
				label="Requested by"
				outlined
				@input="onFilterInputChange"
			></v-select>
			<v-checkbox v-model="filterItems.hideCompleted" class="mx-2" label="Hide completed"></v-checkbox>
			<v-btn class="button button-spacer" @click="onFilterReset" depressed>Reset</v-btn>
		</div>
		<v-data-table
			:headers="computedHeaders"
			:items="items"
			:items-per-page="25"
		></v-data-table>
	</div>
</template>

<script>
import router from "@/router";
import { officeManagementService } from '@/services/officeManagementService.js';

export default {
	name: 'SupportRequestSearch',
	data: function () {
		return {
			headers: [
				{
					text: 'Ticket #',
					align: 'start',
					sortable: true,
					value: 'id'
				},
				{ text: 'Topic', value: 'topicTypeNavigation.text' },
				{ text: 'Office Name', value: 'officeName' },
				{ text: 'Office Abbrev.', value: 'officeAbbreviation' },
				{ text: 'Office Type', value: 'officeType' },
				{ text: 'In Agency', value: 'agencyName' },
				{ text: 'Request By', value: 'requestedBy' },
				{ text: 'Contact Method', value: 'contactType' },
				{ text: 'Date Submitted', value: 'createDate' }
			],
			items: [],
			filterItems: {
				topics: [],
				departments: [],
				agencies: [],
				contactMethods: {},
				requestors: [],
				hideCompleted: true,
				selectedTopicType: "",
				selectedContactType: "",
				selectedDepartment: "",
				selectedAgency: "",
				selectedRequestor: ""
			}
		}
	},
	computed: {
		computedHeaders () {
			let computedHeaders = [...this.headers];

			// If we are not hiding completed, i.e. we are showing items with 2 or more statuses, show a status column.
			if (!this.filterItems.hideCompleted) {
				computedHeaders.push({ text: 'Status', value: 'status' });
			}
			return computedHeaders;
		},
		computedAgencyFilterItems () {
			if (this.filterItems.selectedDepartment === -1) {
				return this.filterItems.agencies;
			} else {
				return this.filterItems.agencies.filter(agency => agency.value === -1 || agency.parentValue === this.filterItems.selectedDepartment);
			}
		}
	},
	methods: {
		onFilterReset: function () {
			this.resetFilters();
			let criteria = this.getSelectedFiltersAsSearchCriteria();
			this.searchSupportRequests(criteria);
		},
		onNavigateSummaryStats: function () {
			router.push({ name: 'admin.supportRequestSummary', params: { previousComponentName: this.$route.name } });
		},
		onFilterInputChange: function () {
			let criteria = this.getSelectedFiltersAsSearchCriteria();
			this.searchSupportRequests(criteria);
		},
		/**
		 * Gets the selected values of the filters bundled up in an object ready to send to the search endpoint.
		 */
		getSelectedFiltersAsSearchCriteria: function () {
			let criteria = {};
			// filter items should only be requested on mounting of the component so set to false by default
			criteria['includeFilterItems'] = false;
			criteria['Status'] = this.filterItems.hideCompleted ? 'Open' : '';
			if (this.filterItems.selectedContactType !== -1) {
				criteria['ContactType'] = this.filterItems.selectedContactType;
			}
			if (this.filterItems.selectedTopicType !== -1) {
				criteria['TopicType'] = this.filterItems.selectedTopicType;
			}
			if (this.filterItems.selectedDepartment !== -1) {
				criteria['Department'] = this.filterItems.selectedDepartment;
			}
			if (this.filterItems.selectedAgency !== -1) {
				criteria['Agency'] = this.filterItems.selectedAgency;
			}
			if (this.filterItems.selectedRequestor !== -1) {
				criteria['RequestedBy'] = this.filterItems.selectedRequestor;
			}

			return criteria;
		},
		/**
		 * Sets all filters to the "All" option.
		 */
		resetFilters: function () {
			this.initializeFilters({});
		},
		/**
		 * Initializes the filters to the starting selected values. Input parameters exist for filter fields
		 * that can be set via params from the summary page. The rest default to "All".
		 * @topicType int - Incoming default selection of the selected
		 */
		initializeFilters: function ({ topicType, contactType, agency }) {
			// Default check All for all filters
			if (topicType) {
				this.filterItems.selectedTopicType = topicType;
			} else {
				this.filterItems.selectedTopicType = -1;
			}
			if (contactType) {
				this.filterItems.selectedContactType = contactType;
			} else {
				this.filterItems.selectedContactType = -1;
			}
			if (agency) {
				this.filterItems.selectedAgency = agency;
			} else {
				this.filterItems.selectedAgency = -1;
			}
			this.filterItems.selectedRequestor = -1;
			this.filterItems.selectedDepartment = -1;

			this.filterItems.hideCompleted = true;
		},
		/**
		 * Runs the search against the api with the input criteria object and binds the resulting data to the component.
		 * @criteria object - The criteria object sent to the search api.
		 * @success function - A callback executed in the event of a successful search call. The function takes in a reference
		 * to "this" (the component) and the search response object.
		 */
		searchSupportRequests: function (criteria, success) {
			let me = this;
			officeManagementService.searchSupportRequests(criteria).then(function (resp) {
				me.items = resp.data.results;
				if (resp.data.filterItems) {
					me.filterItems.topics = [...[ { value: -1, label: 'All' } ], ...resp.data.filterItems.topics];
					me.filterItems.contactMethods = [...[ { value: -1, label: 'All' } ], ...resp.data.filterItems.contactMethods];
					me.filterItems.departments = [...[ { value: -1, label: 'All' } ], ...resp.data.filterItems.departments];
					me.filterItems.agencies = [...[ { value: -1, label: 'All' } ], ...resp.data.filterItems.agencies];
					me.filterItems.requestors = [...[ { value: -1, label: 'All' } ], ...resp.data.filterItems.requestors];
				}

				// If we have a success callback execute it with the service response as input
				if (success) {
					success(me, resp);
				}
			});
		},
		getFilterItems: function () {
			return {
				topics: ['Help', 'Super Help']
			};
		}
	},
	mounted () {
		// let me = this;

		// If another component redirected us here check for incoming filter parameters
		let criteria = { 'includeFilterItems': true };

		if (this.$route.params.topicType) {
			criteria['topicType'] = this.$route.params.topicType;
		}

		if (this.$route.params.contactType) {
			criteria['contactType'] = this.$route.params.contactType;
		}

		if (this.$route.params.agency) {
			criteria['agency'] = this.$route.params.agency;
		}

		// Run the search and bind the results to our data fields
		this.searchSupportRequests(criteria, function (me, response) { me.initializeFilters(criteria); });
	}
}
</script>

<style>
</style>
